import { ContentType } from './enums';
import type { ExternalRefProvider, ExternalRefResourceType } from './enums';
import { z } from 'zod';
import { ContentSharingSummary } from './permission';
import { Tracking } from './tracking';
import type { MessageDraftMode, MessageDraftDefaults } from './content-actions';

export const UNKNOWN_ANCHOR_TITLE = 'Unknown';

const mutableContentTypeValues = [
  ContentType.ANCHOR,
  ContentType.EVENT,
  ContentType.PHOTO,
  ContentType.RECORDING,
  ContentType.VIDEO,
  ContentType.DOCUMENT,
  ContentType.FILE,
  ContentType.URL,
  ContentType.NEO_CHAT,
  ContentType.MESSAGE_EXTRACT,
  ContentType.TEMPLATE,
] as const;
export const mutableContentTypeSchema = z.enum(mutableContentTypeValues);

// ============================================================================
// Response/Output Type Definitions (matching frontend/src/types/content.ts)
// ============================================================================

export interface Creator {
  firstName?: string;
  surname?: string;
}

export interface ContentBase {
  id: number;
  contentType: ContentType;
  title: string;
  description: string;
  timelineAt: string;
  createdAt: string;
  originCreatedAt: string;
  updatedAt: string;
  revision: number;
  tracking: Tracking;
  createdBy: Creator;
  code: string;
  tagsIds: number[];
  recordingCount: number;
  photoCount: number;
  videoCount: number;
  parentAnchorId: number;
  parentAnchorTitle: string;
  sharing?: ContentSharingSummary;
  obligationRollup: ObligationRollup | null;
  externalRefs: ContentExternalRefSummary[];
}

export type ContentPartyType =
  | 'AUTHOR'
  | 'OWNER'
  | 'ASSIGNEE'
  | 'REVIEWER'
  | 'APPROVER'
  | 'CONTRIBUTOR'
  | 'STAKEHOLDER'
  | 'SUBJECT'
  | 'RELATED'
  | 'OBSERVER'
  | 'UNKNOWN';

export interface ContentPartySummary {
  partyId: number;
  role: ContentPartyType;
  email: string;
  name: string | null;
}

export const messageExtractHighlightSchema = z.object({
  quote: z.string().min(1),
  sourceContentId: z.number().int().positive(),
  start: z.number().int().nonnegative().optional(),
  end: z.number().int().nonnegative().optional(),
}).strict();

export type MessageExtractHighlight = z.infer<typeof messageExtractHighlightSchema>;

export type TransmittalActorRole = 'SENDER' | 'ORGANIZER';
export type TransmittalRecipientType =
  | 'TO'
  | 'CC'
  | 'BCC'
  | 'REPLY_TO'
  | 'ATTENDEE'
  | 'OPTIONAL_ATTENDEE'
  | 'REQUIRED_ATTENDEE';

export type TransmittalRoleSummary =
  | {
      kind: 'ACTOR';
      role: TransmittalActorRole;
    }
  | {
      kind: 'RECIPIENT';
      role: TransmittalRecipientType;
    };

export interface TransmittalPartySummary {
  partyId: number;
  role: TransmittalRoleSummary;
  email: string;
  name: string | null;
}

export interface AnchorDriveFolder {
  folderName: string;
  driveId: number;
  driveName: string;
  driveWebUrl: string | null;
  titleFollowsFolder: boolean;
}

export interface Anchor extends ContentBase {
  contentType: 'ANCHOR';
  descendantCount: number;
  driveFolder: AnchorDriveFolder | null;
}

export interface Event extends ContentBase {
  contentType: 'EVENT';
  provider?: 'GOOGLE_CALENDAR' | 'VIVPORT' | 'MICROSOFT_CALENDAR' | null;
  memberExternalAccountId?: number | null;
  providerCalendarId?: string | null;
  providerEventId?: string | null;
  iCalUid?: string | null;
  isAllDay?: boolean;
  timezone?: string | null;
  status?: 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED' | null;
  responseStatus?: 'NEEDS_ACTION' | 'ACCEPTED' | 'DECLINED' | 'TENTATIVE' | null;
  organizer?: MessageConversationAddress | null;
  participants?: MessageConversationAddress[];
  parties: TransmittalPartySummary[];
  recurrenceRule?: string | null;
  location?: string | null;
  conferenceData?: unknown;
  rawPayload?: unknown;
  relationSuggestionMetadata: unknown;
  startDate: string | null;
  endDate: string | null;
  parentAnchorId: number;
  parentAnchorTitle: string;
}

export interface Message extends ContentBase {
  contentType: 'MESSAGE';
  parentCode: string | null;
  rootCode: string | null;
  sortOrder: number;
  depth: number;
  path: string | null;
  memberExternalAccountId: number | null;
  provider: 'GMAIL' | 'OUTLOOK' | 'VIVPORT';
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  providerMessageId: string | null;
  providerThreadId: string | null;
  internetMessageId: string | null;
  inReplyToMessageId: string | null;
  subject: string | null;
  parties: TransmittalPartySummary[];
  from: MessageConversationAddress | null;
  to: MessageConversationAddress[];
  cc: MessageConversationAddress[];
  bcc: MessageConversationAddress[];
  replyTo: MessageConversationAddress[];
  sentAt: string | null;
  receivedAt: string | null;
  isDraft: boolean;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  snippet: string | null;
  textBody: string | null;
  htmlBody: string | null;
  derivedReadableBody?: string | null;
  derivedAuthoredBody?: string | null;
  rawPayload: unknown;
  relationSuggestionMetadata: unknown;
  messageDraftDefaults?: MessageDraftDefaults | null;
  conversation?: MessageConversationProjection | null;
}

export type ObligationRole = 'ACTIONEE' | 'ORIGINATOR' | 'OUTSTANDING';

export interface ObligationRollup {
  role: ObligationRole;
  actionee: number;
  originator: number;
  outstanding: number;
}

export interface ContentExternalRefSummary {
  provider: ExternalRefProvider;
  resourceType: ExternalRefResourceType;
  resourceId: string;
  resourceLabel: string | null;
}

export interface MessageConversationAddress {
  email: string;
  name: string | null;
}

/** Child content linked to a message in the thread (e.g. imported Gmail FILE). */
export interface MessageConversationAttachmentItem {
  id: number;
  code: string;
  contentType: ContentType;
  title: string | null;
  timelineAt: string;
  mimeType: string | null;
  size: number | null;
}

/**
 * Machine-readable resume state for a member-owned OUTBOUND draft thread item.
 * Non-null only when the conversation item is an unsent draft linked to a source
 * message; lets the composer reopen the draft bound to its own content id.
 */
export interface ResumableDraftProjection {
  draftContentId: number;
  sourceMode: MessageDraftMode;
  authoredBody: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
}

export interface MessageConversationItem {
  id: number;
  parentCode: string | null;
  rootCode: string | null;
  sortOrder: number;
  depth: number;
  path: string | null;
  provider: 'GMAIL' | 'OUTLOOK' | 'VIVPORT';
  direction: 'INBOUND' | 'OUTBOUND' | 'INTERNAL';
  memberExternalAccountId: number | null;
  providerMessageId: string | null;
  providerThreadId: string | null;
  internetMessageId: string | null;
  inReplyToMessageId: string | null;
  subject: string | null;
  from: MessageConversationAddress | null;
  parties: TransmittalPartySummary[];
  to: MessageConversationAddress[];
  cc: MessageConversationAddress[];
  bcc: MessageConversationAddress[];
  replyTo: MessageConversationAddress[];
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
  originCreatedAt: string;
  timelineAt: string;
  isDraft: boolean;
  isRead: boolean;
  isStarred: boolean;
  hasAttachments: boolean;
  snippet: string | null;
  textBody: string | null;
  htmlBody: string | null;
  derivedReadableBody?: string | null;
  derivedAuthoredBody?: string | null;
  collapsedSummary: string;
  attachments: MessageConversationAttachmentItem[];
  resumableDraft: ResumableDraftProjection | null;
}

/**
 * Machine-readable edit state for an unsent OUTBOUND draft with no source-message linkage
 * (a compose draft) opened as its own detail page. Non-null only when the source message
 * of this projection is such a draft; lets the compose editor bind to it.
 */
export interface ComposeDraftProjection {
  draftContentId: number;
  memberExternalAccountId: number | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  authoredBody: string;
}

export interface MessageConversationProjection {
  sourceMessageId: number;
  rootCode: string | null;
  provider: 'GMAIL' | 'OUTLOOK' | 'VIVPORT';
  providerThreadId: string | null;
  memberExternalAccountId: number | null;
  itemCount: number;
  items: MessageConversationItem[];
  composeDraft: ComposeDraftProjection | null;
}
